import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getCurrentUserRole } from 'features/auth/ducks/selectors'
import PageHeader from 'components/atoms/PageHeader'
import styled from 'styled-components'
import ProfileInfo from 'features/profileSettings/components/ProfileInfo'
import StatBox from 'features/dashboard/components/StatBox'
import { isEmpty, pathOr } from 'ramda'
import PeriodSwitch from 'components/atoms/PeriodSwitch'
import { getBillingListRoutine } from 'features/billingList/ducks/actions'
import {
  getBillingListStatus,
  getTransactionsList,
  hasStripeSubscription
} from 'features/billingList/ducks/selectors'
import BillingListTable from 'features/billingList/components/BillingListTable'
import EmptyState from 'features/billingList/components/EmptyState'
import {
  getDashboardLoading,
  getDashboardStats
} from 'features/dashboard/ducks/selectors'
import { fetchDashboardStatisticsRoutine } from 'features/dashboard/ducks/actions'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import CloseIcon from '@mui/icons-material/Close'
import PrimaryButton from 'components/atoms/PrimaryButton'
import { createBillingPortalSession } from 'services/billingService'
import { toast } from 'react-toastify'
import { SkeletonRows } from 'components/Skeleton'

const SectionTitle = ({ children }) => {
  return (
    <SectionTitleWrapper>
      <SectionTitleText>{children}</SectionTitleText>
      <SectionUnderline />
    </SectionTitleWrapper>
  )
}

const BillingList = () => {
  const userRole = useSelector(getCurrentUserRole)
  const dashboardStats = useSelector(getDashboardStats)
  const dashboardLoading = useSelector(getDashboardLoading)
  const transactions = useSelector(getTransactionsList)
  const billingListStatus = useSelector(getBillingListStatus)
  const canManageStripeSubscription = useSelector(hasStripeSubscription)
  const dispatch = useDispatch()
  const [period, setPeriod] = useState('month')
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false)
  const [isOpeningBillingPortal, setIsOpeningBillingPortal] = useState(false)

  const handlePeriodChange = period => setPeriod(period)
  const isInitialLoading =
    isEmpty(transactions) &&
    (billingListStatus === 'idle' || billingListStatus === 'loading')
  const hasLoadingError =
    isEmpty(transactions) && billingListStatus === 'failure'
  const isStatisticsLoading =
    billingListStatus === 'idle' || dashboardLoading

  const retryLoading = () => {
    dispatch(fetchDashboardStatisticsRoutine())
    dispatch(getBillingListRoutine())
  }

  const handleManageBilling = async () => {
    setIsOpeningBillingPortal(true)

    try {
      const response = await createBillingPortalSession()
      window.location.assign(response.data.data.url)
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Could not open subscription management.')
      setIsOpeningBillingPortal(false)
    }
  }

  useEffect(() => {
    dispatch(fetchDashboardStatisticsRoutine())
    dispatch(getBillingListRoutine())
  }, [])

  return (
    <PageWrapper>
      <ContentWrapper>
        <PageHeaderWrapper>
          <HeaderWrapper>
            <PageHeader>List of transactions</PageHeader>
            <MobileMenuButton onClick={() => setMobileDrawerOpen(true)}>
              <AccountCircleIcon />
            </MobileMenuButton>
          </HeaderWrapper>
          <PeriodSwitch onChange={handlePeriodChange} />
        </PageHeaderWrapper>
        <Statistics>
          {isStatisticsLoading
            ? (
              <>
                <StatSkeleton><SkeletonRows rows={2} widths={['70%', '42%']} /></StatSkeleton>
                {userRole === 'agent' && (
                  <StatSkeleton><SkeletonRows rows={2} widths={['65%', '35%']} /></StatSkeleton>
                )}
                <StatSkeleton><SkeletonRows rows={2} widths={['75%', '30%']} /></StatSkeleton>
              </>
              )
            : (
              <>
                <StatBox
                  title='Amount spent on Building Passport'
                  value={pathOr(0, ['amountSpent', period, 'formatted'], dashboardStats)}
                  period={`this ${period}`}
                  primary
                />
                {userRole === 'agent' && (
                  <StatBox
                    title='Number of Clients'
                    value={pathOr('', ['numberOfClients', period], dashboardStats)}
                    period={`this ${period}`}
                  />
                )}
              <StatBox
                title='Number of Building Passports'
                value={pathOr('', ['buildingPassports', period], dashboardStats)}
                period={`this ${period}`}
              />
              </>
              )}
        </Statistics>
        <SectionTitle>Billing list</SectionTitle>
        {!isInitialLoading && canManageStripeSubscription && (
          <BillingActions>
            <BillingPortalButton
              onClick={handleManageBilling}
              disabled={isOpeningBillingPortal}
              isLoading={isOpeningBillingPortal}
              loadingText='Opening...'
              aria-label='Manage subscriptions and payment methods'
              title='Manage subscriptions and payment methods'
            >
              Manage billing
            </BillingPortalButton>
          </BillingActions>
        )}
        {isInitialLoading && (
          <BillingLoading aria-busy='true' aria-label='Loading billing transactions'>
            <SkeletonRows rows={5} widths={['18%', '10%', '20%', '16%', '18%']} />
          </BillingLoading>
        )}
        {hasLoadingError && (
          <LoadError role='alert'>
            <LoadErrorTitle>We couldn&apos;t load your billing history.</LoadErrorTitle>
            <RetryButton onClick={retryLoading}>Try again</RetryButton>
          </LoadError>
        )}
        {!isInitialLoading && !hasLoadingError && (
          isEmpty(transactions)
            ? <EmptyState />
            : <BillingListTable transactions={transactions} />
        )}
      </ContentWrapper>
      <DetailsWrapper>
        <ProfileInfo />
      </DetailsWrapper>

      <MobileDrawerOverlay
        isOpen={mobileDrawerOpen}
        onClick={() => setMobileDrawerOpen(false)}
      />
      <MobileDrawer isOpen={mobileDrawerOpen}>
        <MobileDrawerHeader>
          <MobileDrawerTitle>Your Profile</MobileDrawerTitle>
          <MobileDrawerClose onClick={() => setMobileDrawerOpen(false)}>
            <CloseIcon />
          </MobileDrawerClose>
        </MobileDrawerHeader>
        <MobileDrawerContent>
          <ProfileInfo />
        </MobileDrawerContent>
      </MobileDrawer>
    </PageWrapper>
  )
}

export default BillingList

const PageWrapper = styled.div`
  display: flex;
`
const ContentWrapper = styled.div`
  width: 100%;
  padding-right: 300px;
  overflow: hidden;

  @media (max-width: 768px) {
    padding-right: 0;
  }
`

const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;

  @media (max-width: 768px) {
    justify-content: flex-start;
  }
`

const MobileMenuButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 8px;
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.2s ease;
  color: ${({ theme }) => theme.colors.haiti || '#333'};
  margin-left: auto;

  @media (max-width: 768px) {
    display: flex;
    margin-left: auto;
    margin-right: 0;
  }

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }

  svg {
    font-size: 28px;
  }
`

const DetailsWrapper = styled.div`
  width: 280px;
  position: fixed;
  top: ${({ theme }) => theme.dimensions.topBarHeight};
  right: 0;
  bottom: 0;
  background-color: ${({ theme }) => theme.colors.lighterGrey};
  padding: 25px 20px;
  overflow-y: auto;

  @media (max-width: 768px) {
    display: none;
  }
`

const Statistics = styled.div`
  display: flex;
  align-items: center;
  margin-top: 40px;

  & > div:not(:first-of-type) {
    margin-left: 20px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    margin-top: 24px;

    & > div:not(:first-of-type) {
      margin-left: 0;
    }
  }
`

const StatSkeleton = styled.div`
  width: 300px;
  min-height: 126px;
  padding: 28px;
  box-sizing: border-box;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.white};
`

const BillingLoading = styled.div`
  padding: 24px 16px;
  box-sizing: border-box;
  background: ${({ theme }) => theme.colors.white};
`

const LoadError = styled.div`
  padding: 36px;
  text-align: center;
  background: ${({ theme }) => theme.colors.white};
`

const LoadErrorTitle = styled.div`
  margin-bottom: 18px;
  font-size: 18px;
  color: ${({ theme }) => theme.colors.haiti};
`

const RetryButton = styled.button`
  border: 0;
  border-radius: 20px;
  padding: 10px 24px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.white};
  background: ${({ theme }) => theme.colors.haiti};
`

const SectionTitleWrapper = styled.div`
  margin: 40px 0 20px;

  @media (max-width: 768px) {
    margin: 24px 0 16px;
  }
`

const BillingActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: -8px 0 20px;

  @media (max-width: 768px) {
    margin-top: 0;
  }
`

const BillingPortalButton = styled(PrimaryButton)`
  width: auto;
  min-width: 160px;
  max-width: 100%;
  min-height: 42px;
  padding: 8px 20px !important;
  white-space: nowrap;

  @media (max-width: 480px) {
    width: 100%;
    min-width: 0;
  }
`

const SectionTitleText = styled.div`
  font-size: 18px;
  color: ${({ theme }) => theme.colors.haiti};
  margin-bottom: 10px;
`

const SectionUnderline = styled.div`
  width: 50px;
  height: 4px;
  background-color: ${({ theme }) => theme.colors.secondary};
`

const PageHeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`

const MobileDrawerOverlay = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1298;
    backdrop-filter: blur(2px);
    transition: opacity 0.3s ease;
    opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  }
`

const MobileDrawer = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 0;
    right: 0;
    width: 280px;
    max-width: 85vw;
    height: 100vh;
    background: ${({ theme }) => theme.colors.lighterGrey || '#f8f9fa'};
    box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
    z-index: 1299;
    transform: translateX(${({ isOpen }) => (isOpen ? '0' : '100%')});
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    overflow: hidden;
  }
`

const MobileDrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;
  background: #ffffff;
  min-height: 56px;
`

const MobileDrawerTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti || '#333'};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const MobileDrawerClose = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 8px;
  cursor: pointer;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.haiti || '#333'};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }

  svg {
    font-size: 24px;
  }
`

const MobileDrawerContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 50px 20px;
  -webkit-overflow-scrolling: touch;
`
